import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Modal, Spin, Tag } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import BillService from "../../services/Billservice";
import { notify } from "../Customs/NotifyProvider";
import "./GuestOrdersQueue.css";

const { TextArea } = Input;

const STATUS_COLORS = {
  pending: "orange",
  confirmed: "green",
  completed: "blue",
  cancelled: "red",
};

// Every guest order becomes a parcel item once confirmed (see
// demo-restaurant-bill-api's guestOrderModel.js), so it earns the same
// parcel/additional charge and tax as any other Room Service item — this
// mirrors billingModel.js's _calcTotals exactly, so what staff see here
// matches what actually lands on the room's bill after Confirm.
const computeOrderTotal = (items, settings = {}) => {
  const subtotal = items.reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0);
  const qty = items.reduce((sum, item) => sum + item.quantity, 0);
  const parcelChargeTotal = qty * Number(settings.parcel_charge || 0);
  const additionalChargeTotal = qty * Number(settings.additional_charge || 0);
  const cgstAmount = (subtotal * Number(settings.cgst_tax || 0)) / 100;
  const sgstAmount = (subtotal * Number(settings.sgst_tax || 0)) / 100;
  const grandTotal = subtotal + parcelChargeTotal + additionalChargeTotal + cgstAmount + sgstAmount;

  return { subtotal, qty, parcelChargeTotal, additionalChargeTotal, cgstAmount, sgstAmount, grandTotal };
};

function BreakdownRows({ totals, settings }) {
  return (
    <div className="guest-order-breakdown">
      <div className="guest-order-breakdown-row">
        <span>Items Subtotal</span>
        <span>₹{totals.subtotal.toFixed(2)}</span>
      </div>
      {totals.parcelChargeTotal > 0 && (
        <div className="guest-order-breakdown-row">
          <span>Parcel Charge ({totals.qty} qty)</span>
          <span>₹{totals.parcelChargeTotal.toFixed(2)}</span>
        </div>
      )}
      {totals.additionalChargeTotal > 0 && (
        <div className="guest-order-breakdown-row">
          <span>Additional Charge ({totals.qty} qty)</span>
          <span>₹{totals.additionalChargeTotal.toFixed(2)}</span>
        </div>
      )}
      {totals.cgstAmount > 0 && (
        <div className="guest-order-breakdown-row">
          <span>CGST ({settings?.cgst_tax || 0}%)</span>
          <span>₹{totals.cgstAmount.toFixed(2)}</span>
        </div>
      )}
      {totals.sgstAmount > 0 && (
        <div className="guest-order-breakdown-row">
          <span>SGST ({settings?.sgst_tax || 0}%)</span>
          <span>₹{totals.sgstAmount.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}

// Search + category picker for adding a NEW item to the order — same shape
// as the real Bill editor's "Add Parcel Items" picker (BillEditorNoContext's
// ProductPickerModal): search box, category chips, click a card to add it
// (or bump its quantity if it's already in the order).
function AddItemModal({ open, onClose, menuItems, categories, onPick }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  const categoryNames = useMemo(() => ["All", ...categories.map((c) => c.name)], [categories]);

  const filtered = useMemo(
    () =>
      menuItems.filter((item) => {
        const matchesCategory = category === "All" || item.category === category;
        const matchesSearch = String(item.name || "")
          .toLowerCase()
          .includes(search.toLowerCase());
        return matchesCategory && matchesSearch;
      }),
    [menuItems, search, category]
  );

  return (
    <Modal title="Add Item" open={open} onCancel={onClose} footer={null} width={640} destroyOnClose>
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search items..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        allowClear
        size="large"
        style={{ marginBottom: 12 }}
      />

      <div className="guest-order-cat-scroll">
        {categoryNames.map((name) => (
          <button
            key={name}
            type="button"
            className={`guest-order-cat-chip${category === name ? " active" : ""}`}
            onClick={() => setCategory(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="guest-order-picker-grid">
        {filtered.length === 0 && <p className="guest-order-no-items">No items found</p>}
        {filtered.map((item) => (
          <button
            type="button"
            className="guest-order-picker-card"
            key={item.id}
            onClick={() => onPick(item)}
          >
            <span className="guest-order-picker-name">{item.name}</span>
            <span className="guest-order-picker-price">₹{Number(item.price).toFixed(0)}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

// The StayMaster guest app lets a checked-in guest place a food order from
// their room; it lands here as "pending" until a staff member reviews it.
// Confirm adds the items into that room's open bill (opening one if it
// doesn't have one yet) — see demo-restaurant-bill-api's guestOrderModel.
// Edit is only ever offered while still "pending" — once confirmed, staff
// manage the real charge directly in the normal Room Service bill editor.
export default function GuestOrdersQueue({
  onOrderActioned,
  generalSettings,
  menuItems = [],
  categories = [],
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);

  const [editingOrder, setEditingOrder] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await BillService.getGuestOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      notify.error("Unable to Load Guest Orders", err.message || "Something went wrong.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleConfirm = async (order) => {
    setActioningId(order.id);
    try {
      await BillService.confirmGuestOrder(order.id);
      notify.success(
        "Order Confirmed",
        `Added to ${order.room_label || "the room"}'s bill.`
      );
      await load();
      onOrderActioned?.();
    } catch (err) {
      notify.error("Unable to Confirm Order", err.message || "Something went wrong.");
    } finally {
      setActioningId(null);
    }
  };

  const handleDecline = async (order) => {
    setActioningId(order.id);
    try {
      await BillService.cancelGuestOrder(order.id);
      notify.success("Order Declined", "The guest's order was declined.");
      await load();
      onOrderActioned?.();
    } catch (err) {
      notify.error("Unable to Decline Order", err.message || "Something went wrong.");
    } finally {
      setActioningId(null);
    }
  };

  // Only the items the guest actually ordered show up by default — the
  // full menu is one tap away via "+ Add Item", not dumped on screen at
  // once (that's what made the old picker confusing).
  const openEdit = (order) => {
    setEditingOrder(order);
    setEditNotes(order.notes || "");
    setEditItems(
      order.items.map((item) => ({
        id: item.menu_item_id,
        name: item.menu_item_name,
        price: item.menu_item_price,
        quantity: item.quantity,
      }))
    );
  };

  const closeEdit = () => {
    setEditingOrder(null);
    setEditItems([]);
    setAddItemOpen(false);
  };

  const adjustEditItemQty = (itemId, delta) => {
    setEditItems((previous) =>
      previous
        .map((item) => (item.id === itemId ? { ...item, quantity: item.quantity + delta } : item))
        .filter((item) => item.quantity > 0)
    );
  };

  const removeEditItem = (itemId) => {
    setEditItems((previous) => previous.filter((item) => item.id !== itemId));
  };

  const handlePickItem = (menuItem) => {
    setEditItems((previous) => {
      const existing = previous.find((item) => item.id === menuItem.id);
      if (existing) {
        return previous.map((item) =>
          item.id === menuItem.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...previous, { id: menuItem.id, name: menuItem.name, price: menuItem.price, quantity: 1 }];
    });
  };

  const editTotals = useMemo(
    () => computeOrderTotal(editItems, generalSettings),
    [editItems, generalSettings]
  );

  const handleSaveEdit = async () => {
    if (!editItems.length) {
      notify.warning("Add at Least One Item", "An order can't be saved with nothing in it.");
      return;
    }

    setEditSubmitting(true);
    try {
      await BillService.updateGuestOrder(editingOrder.id, {
        notes: editNotes.trim() || null,
        items: editItems.map((item) => ({ menu_item_id: item.id, quantity: item.quantity })),
      });
      notify.success("Order Updated", "The guest's order was updated.");
      closeEdit();
      await load();
    } catch (err) {
      notify.error("Unable to Update Order", err.message || "Something went wrong.");
    } finally {
      setEditSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-empty">
        <Spin size="large" />
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="page-empty">
        <Empty description="No guest orders yet" />
      </div>
    );
  }

  return (
    <>
      <div className="guest-orders-grid">
        {orders.map((order) => {
          const totals = computeOrderTotal(
            order.items.map((item) => ({ ...item, price: item.menu_item_price })),
            generalSettings
          );

          return (
            <div className="guest-order-card" key={order.id}>
              <div className="guest-order-card-head">
                <strong className="guest-order-room-label">{order.room_label || "Room"}</strong>

                <div className="guest-order-card-subrow">
                  <div className="guest-order-card-head-text">
                    <p className="guest-order-guest">{order.guest_name}</p>
                    {order.guest_phone && (
                      <p className="guest-order-phone">{order.guest_phone}</p>
                    )}
                  </div>
                  <Tag color={STATUS_COLORS[order.status] || "default"}>{order.status}</Tag>
                </div>
              </div>

              <div className="guest-order-items">
                {order.items.map((item) => (
                  <div className="guest-order-item-row" key={item.id}>
                    <span className="guest-order-item-name">
                      {item.quantity} × {item.menu_item_name || "Item"}
                    </span>
                    <span className="guest-order-item-price">
                      ₹{(Number(item.menu_item_price || 0) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {order.notes && <p className="guest-order-notes">"{order.notes}"</p>}

              <BreakdownRows totals={totals} settings={generalSettings} />

              <div className="guest-order-total">Total: ₹{totals.grandTotal.toFixed(2)}</div>

              {order.status === "pending" && (
                <div className="guest-order-actions">
                  <div className="guest-order-actions-secondary">
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openEdit(order)}
                      disabled={actioningId === order.id}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => handleDecline(order)}
                      loading={actioningId === order.id}
                    >
                      Decline
                    </Button>
                  </div>
                  <Button
                    block
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => handleConfirm(order)}
                    loading={actioningId === order.id}
                  >
                    Confirm
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        title="Edit Guest Order"
        open={Boolean(editingOrder)}
        onCancel={closeEdit}
        footer={null}
        destroyOnClose
        width={520}
      >
        <div className="guest-order-edit-items">
          {editItems.length === 0 && (
            <p className="guest-order-no-items">No items yet — add one below.</p>
          )}

          {editItems.map((item) => (
            <div className="guest-order-edit-row" key={item.id}>
              <div className="guest-order-edit-info">
                <span>{item.name}</span>
                <span className="guest-order-edit-price">₹{Number(item.price).toFixed(0)}</span>
              </div>

              <div className="guest-order-edit-qty">
                <button
                  type="button"
                  className="guest-order-stepper-btn"
                  onClick={() => adjustEditItemQty(item.id, -1)}
                >
                  <MinusOutlined />
                </button>
                <span>{item.quantity}</span>
                <button
                  type="button"
                  className="guest-order-stepper-btn"
                  onClick={() => adjustEditItemQty(item.id, 1)}
                >
                  <PlusOutlined />
                </button>
              </div>

              <button
                type="button"
                className="guest-order-remove-btn"
                onClick={() => removeEditItem(item.id)}
                aria-label={`Remove ${item.name}`}
              >
                <DeleteOutlined />
              </button>
            </div>
          ))}
        </div>

        <Button
          block
          icon={<PlusOutlined />}
          className="guest-order-add-item-btn"
          onClick={() => setAddItemOpen(true)}
        >
          Add Item
        </Button>

        <div className="guest-order-edit-notes">
          <label>Notes (optional)</label>
          <TextArea
            rows={2}
            placeholder="Any special requests?"
            value={editNotes}
            onChange={(event) => setEditNotes(event.target.value)}
          />
        </div>

        {editItems.length > 0 && <BreakdownRows totals={editTotals} settings={generalSettings} />}

        <div className="guest-order-edit-footer">
          <strong>Total: ₹{editTotals.grandTotal.toFixed(2)}</strong>
          <div>
            <Button onClick={closeEdit} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveEdit}
              loading={editSubmitting}
              disabled={!editItems.length}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      <AddItemModal
        open={addItemOpen}
        onClose={() => setAddItemOpen(false)}
        menuItems={menuItems}
        categories={categories}
        onPick={handlePickItem}
      />
    </>
  );
}
